import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Box, IconButton, Typography, Select, MenuItem, Menu, FormControl, InputLabel } from '@mui/material';
import { MdChevronLeft } from 'react-icons/md';
import { useUserSlice } from '#/store/slices/user/userSlice';
import { useSwitchRoleMutation } from '#/store/slices/api/auth.api';
import CenteredLoading from '../CenteredLoading';
import { useSnackbar } from '#/context/snackbar';
import LogoutModal from './components/LogoutModal';
import { Role } from '#/store/slices/api/types/user';

interface HeaderProps {
  drawerOpen: boolean;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
}

function Header({ drawerOpen, setDrawerOpen }: HeaderProps) {
  const navigate = useNavigate();
  const { showSnackbar, showApiErrorSnackbar } = useSnackbar();
  const { activeRole, givenName } = useUserSlice();

  const [switchRole, { isLoading }] = useSwitchRoleMutation();

  const [menu, setMenu] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  const appBarRef = useRef(null);
  const buttonRef = useRef(null);
  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleSwitchRole = (role: Role) => {
    if (role === activeRole) return;

    switchRole({ role }).unwrap()
      .then(() => {
        showSnackbar(`Switched to ${role} role`, 'success');
        setMenu(false);
      })
      .catch(showApiErrorSnackbar('Could not switch role'));
  };

  const handleProfile = () => {
    navigate('/profile');
    setMenu(false);
  };

  const handleLogout = () => {
    setLogoutModalOpen(true);
    setMenu(false);
  };

  return (
    <AppBar
        elevation={2}
        position="fixed"
        sx={{
          display: 'flex',
          bgcolor: '#F9F2EC',
          zIndex: (theme) => theme.zIndex.drawer + 1
        }}
        ref={appBarRef}
      >
      <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
        <Box display="flex" alignItems="center">
          <IconButton
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            size="small"
            sx={{ mr: 2 }}
          >
            <MdChevronLeft style={{ transform: drawerOpen ? '' : 'rotate(-180deg)', transition: '300ms' }} />
          </IconButton>
          <img src="/images/logos/waym-black.png" height={30} />
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setMenu(true)} ref={buttonRef}>
            <Box height={31} width={31} bgcolor="secondary.main" borderRadius="50%" display="flex" alignItems="center" justifyContent="center">
              <Typography color="black" fontWeight="bold" fontSize={14} lineHeight={1}>{givenName[0]}</Typography>
            </Box>
          </IconButton>
          <Menu
            open={menu}
            onClose={() => setMenu(false)}
            anchorEl={buttonRef.current}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <Box sx={{ p: 1 }}>
              {isLoading ? (
                <Box width={150}>
                  <CenteredLoading />
                </Box>
              ) : (
                <FormControl>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={activeRole}
                    onChange={(e) => handleSwitchRole(e.target.value)}
                    size="small"
                    variant="outlined"
                    fullWidth
                    sx={{ width: 150 }}
                  >
                    <MenuItem value={Role.ADMINISTRATOR}>Administrator</MenuItem>
                    <MenuItem value={Role.INSTRUCTOR}>Educator</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
            <MenuItem onClick={handleProfile}>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <LogoutModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
      />
    </AppBar>
  );
}

export default Header;
